import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Space,
  Typography,
  List,
  Slider,
  Input,
  Button,
  message,
  DatePicker,
  Tag,
  Image,
  theme,
  Tooltip,
} from "antd";
import { ClockCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";
import {
  getNurseCommonData,
  updateNurseCommonData,
} from "../../../services/nurse.services";
import useQuery from "../../../hooks/useQuery";
import dayjs from "dayjs";
import { convertUtcToIst } from "../../../helpers/utility";
import inputStyles from "../../../styles/Custom/Input.module.css";
import { dietProgressBar } from "../../../helpers/medicineKeys";
import "./DietChart.css";
import NoDataPage from "../../NoDataPages/NoDataPage";
import DietImage from "../../../assets/images/nurseIcons/diet.svg";
import calenderIcon from "../../../assets/images/nurseIcons/calender.svg";
import LoadingIndicator from "../../Loaders/LoadingIndicator";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import useIsMobile from "../../../hooks/useIsMobile";
import useReceptionistMobilePickerProps from "../../Receptionist/receptionistMobilePickerProps";

dayjs.extend(utc);
dayjs.extend(timezone);

const { useToken } = theme;

const dateFormat = "YYYY-MM-DD";

const DietChart = () => {
  const { token } = useToken();
  const isMobile = useIsMobile();
  const mobilePickerProps = useReceptionistMobilePickerProps();
  const curDate = dayjs().format(dateFormat);
  const params = useQuery();
  const [notes, setNotes] = useState({});
  const [sliders, setSliders] = useState({});
  const [dietType, setDietType] = useState("");
  const [dietComments, setDietComments] = useState("");
  const [dietData, setDietData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [date, setDate] = useState(curDate);

  const refreshPage = () => {
    setRefresh((prev) => !prev);
  };

  function disabledDate(current) {
    return current && current > dayjs().endOf("day");
  }

  const onDateChange = (date, dateString) => {
    setDate(date ? dateString : null);
  };

  useEffect(() => {
    getNurseCommonData(params?.patient, "diet", date)
      .then((res) => {
        const processedData = res?.data?.data?.map((item) => ({
          ...item,
          sliderValue:
            item?.action === "nothing"
              ? 0
              : item?.action === "one_quarter"
              ? 0.25
              : item?.action === "half"
              ? 0.5
              : item?.action === "three_quarters"
              ? 0.75
              : item?.action === "full"
              ? 1
              : 0,
          notes: item.notes || "",
          editable: item?.processed ? false : true,
        }));
        setDietData(processedData);
        setDietType(res?.data?.diet_type || "");
        setDietComments(res?.data?.comments || "");
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [refresh, date]);

  const handleNoteChange = (id, value) => {
    setNotes((prevNotes) => ({
      ...prevNotes,
      [id]: value,
    }));
  };

  const handleSliderChange = (id, value) => {
    setSliders((prevSliders) => ({
      ...prevSliders,
      [id]: value,
    }));
  };

  const handleSubmit = (item) => {
    const data = {
      patient: params.patient,
      remarks: notes[item.id],
      action:
        sliders[item.id] === 0
          ? "nothing"
          : sliders[item.id] === 0.25
          ? "one_quarter"
          : sliders[item.id] === 0.5
          ? "half"
          : sliders[item.id] === 0.75
          ? "three_quarters"
          : sliders[item.id] === 1
          ? "full"
          : "nothing",
    };

    updateNurseCommonData(item.id, data)
      .then((res) => {
        message.success(res.data.message);
        refreshPage();
      })
      .catch((err) => {
        message.error(err.response.data.message);
      });
  };

  const toggleEdit = (item) => {
    setDietData((prevData) =>
      prevData.map((dataItem) =>
        dataItem.id === item.id ? { ...dataItem, editable: true } : dataItem
      )
    );
  };

  const tipFormatter = (value) => {
    switch (value) {
      case 0:
        return "Nothing";
      case 0.25:
        return "1/4";
      case 0.5:
        return "1/2";
      case 0.75:
        return "3/4";
      case 1:
        return "Full";
      default:
        return "";
    }
  };

  const renderTimeTag = (item) => (
    <Tag color="purple" style={{ fontSize: "14px", fontWeight: "500", margin: 0 }}>
      <Space style={{ fontSize: "12px" }}>
        <ClockCircleOutlined />
        {item.action_time && convertUtcToIst(item.action_time, false)}{" "}
      </Space>
    </Tag>
  );

  const renderFoodName = (item) => (
    <Typography.Text className="diet-chart-food-name">
      {item?.data?.name}
      {item?.data?.quantity && (
        <Tag color="geekblue" style={{ marginLeft: 8 }}>
          {item?.data?.quantity}
        </Tag>
      )}
    </Typography.Text>
  );

  const renderFoodMeta = (item) => (
    <>
      {item?.data?.comments && (
        <Tag color="yellow">
          <Typography
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              fontSize: "12px",
            }}
          >
            {item?.data?.comments}
          </Typography>
        </Tag>
      )}
      {item?.data?.instructions && (
        <Tag color="yellow">
          <Typography
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {item?.data?.instructions}
          </Typography>
        </Tag>
      )}
    </>
  );

  return (
    <div className="diet-chart-page">
      <div className="diet-chart-toolbar">
        <DatePicker
          style={{
            color: "black",
            border: `1px solid ${token.colorPrimary}`,
            height: "38px",
            width: isMobile ? "100%" : "150px",
          }}
          disabledDate={disabledDate}
          className={`diet-chart-date-picker ${inputStyles.mdDateInput}`}
          onChange={onDateChange}
          defaultValue={dayjs(curDate)}
          allowClear={false}
          suffixIcon={<Image width={20} src={calenderIcon} preview={false} />}
          {...mobilePickerProps}
          popupStyle={
            isMobile
              ? { maxWidth: "calc(100vw - 16px)", width: "calc(100vw - 16px)" }
              : undefined
          }
        />
      </div>

      {loading ? (
        <LoadingIndicator message={"Loading diet data, please wait..."} />
      ) : dietData?.length > 0 ? (
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <List
              className="diet-chart-list"
              style={{
                background: "#ffff",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
              loading={loading}
              header={
                <>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    Diet Chart
                  </Typography.Title>

                  {dietData?.length > 0 && (
                    <Row
                      style={{
                        marginTop: 8,
                        background: "#fafafa",
                        padding: "6px 8px",
                        borderRadius: "3px",
                      }}
                    >
                      <Col span={24} style={{ marginBottom: "4px" }}>
                        <Typography.Text strong>Diet Type:</Typography.Text>{" "}
                        <Typography.Text>{dietType || "N/A"}</Typography.Text>
                      </Col>

                      <Col span={24}>
                        <Typography.Text strong>Comments:</Typography.Text>
                        <Typography.Text
                          style={{ display: "block", whiteSpace: "pre-wrap" }}
                        >
                          {dietComments || "N/A"}
                        </Typography.Text>
                      </Col>
                    </Row>
                  )}
                </>
              }
              itemLayout="vertical"
              bordered
              dataSource={dietData}
              renderItem={(item) => (
                <List.Item key={item.id} className="diet-chart-list-item">
                  <Row gutter={[12, 12]} align="middle" style={{ width: "100%" }}>
                    {isMobile ? (
                      <Col xs={24} className="diet-chart-top-col">
                        <div className="diet-chart-mobile-top">
                          {renderTimeTag(item)}
                          {renderFoodName(item)}
                        </div>
                        {(item?.data?.comments || item?.data?.instructions) && (
                          <Space
                            direction="vertical"
                            size={6}
                            className="diet-chart-food-meta"
                          >
                            {renderFoodMeta(item)}
                          </Space>
                        )}
                      </Col>
                    ) : (
                      <>
                        <Col sm={6} md={4} lg={4}>
                          {renderTimeTag(item)}
                        </Col>
                        <Col sm={10} md={4} lg={4}>
                          <Space direction="vertical">
                            {renderFoodName(item)}
                            {renderFoodMeta(item)}
                          </Space>
                        </Col>
                      </>
                    )}

                    <Col xs={24} sm={14} md={9} lg={8}>
                      {!item?.editable ? (
                        <>{dietProgressBar(item?.action)}</>
                      ) : (
                        <div className="diet-chart-slider-wrap">
                          <Slider
                            marks={{ 0: "Nothing", 1: "Full" }}
                            step={0.25}
                            max={1}
                            min={0}
                            dots={true}
                            className="diet-chart-slider"
                            style={{ width: isMobile ? "100%" : "90%" }}
                            onChange={(value) =>
                              handleSliderChange(item.id, value)
                            }
                            value={
                              sliders[item.id] !== undefined
                                ? sliders[item.id]
                                : item.sliderValue
                            }
                            tipFormatter={tipFormatter}
                          />
                        </div>
                      )}
                    </Col>

                    <Col xs={24} sm={12} md={4} lg={6}>
                      {!item.editable ? (
                        <Typography style={{ whiteSpace: "pre-wrap" }}>
                          {item.remarks ? item.remarks : " "}
                        </Typography>
                      ) : (
                        <Input.TextArea
                          style={{ height: "40px" }}
                          placeholder="Enter Remarks"
                          value={
                            notes[item.id] !== undefined
                              ? notes[item.id]
                              : item.remarks || ""
                          }

                          onChange={(e) =>
                            handleNoteChange(item.id, e.target.value)
                          }
                        />
                      )}
                    </Col>

                    <Col xs={24} sm={12} md={2} lg={2} className="diet-chart-actions-col">
                      {item.editable ? (
                        <Button
                          className="inv-btn"
                          onClick={() => handleSubmit(item)}
                          type="primary"
                          ghost
                          style={{ height: "30px", width: "75px" }}
                        >
                          Submit
                        </Button>
                      ) : (
                        curDate === date && (
                          <Space>
                            <Button
                              className="inv-btn"
                              onClick={() => toggleEdit(item)}
                              type="primary"
                              ghost
                              style={{ height: "30px", width: "75px", display: "flex",alignItems: "center",justifyContent: "center",padding: 0, }}
                            >
                              <EditIcon /> Edit
                            </Button>
                    
                            <Tooltip
                              placement="top"
                              title={
                                <div>
                                  <div>
                                    <strong> Updated at:</strong>{" "}
                                    {item?.nurse_data?.last_updated
                                      ? dayjs
                                          .utc(item.nurse_data.last_updated)
                                          .tz("Asia/Kolkata")
                                          .format("DD-MM-YYYY hh:mm A")
                                      : "-"}
                                  </div>
                            
                                  <div style={{ marginTop: 4 }}>
                                    <strong>Processed By:</strong>{" "}
                                    {item?.nurse_data?.processed_by_name || "-"}
                                  </div>
                                </div>
                              }
                            >
                              <InfoCircleOutlined
                                style={{
                                  fontSize: "16px",
                                  color: "#1677ff",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>

                          </Space>
                        )
                      )}
                    </Col>

                  </Row>
                </List.Item>
              )}
            />
          </Col>
        </Row>
      ) : (
        <Row
          className="diet-chart-empty"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: isMobile ? "50vh" : "70vh",
          }}
        >
          <Col span={24} style={{ textAlign: "center" }}>
            <NoDataPage
              title="No Diet Found"
              subtitle="It looks like there are no diet at the moment."
              image={DietImage}
            />
          </Col>
        </Row>
      )}
    </div>
  );
};

export default DietChart;
